import { includes, mergeDeepRight } from "ramda";
import { useSelector } from "react-redux";
import { AUTH_BASE_URL } from "../props";
import { allPass, always, identity, ifElse, pickBy, pipe } from "ramda";
import { isBlank, notBlank } from "./ext";
import querystring from "querystring";
import { isUUID, notUUID } from "./mediaUtil";
import axios from "axios";
import { doGet } from "../common/fetchServices";

export class FetchError extends Error {
  constructor(status, message, body, ...params) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(...params);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchError);
    }

    this.name = "FetchError";
    // Custom debugging information
    this.status = status;
    this.message = message;
    this.body = body;
  }
}

export class UnauthorizedError extends Error {
  constructor(message, ...params) {
    super(...params);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UnauthorizedError);
    }

    this.name = "UnauthorizedError";
    this.message = message;
  }
}

export class NotFoundError extends Error {
  constructor(message, ...params) {
    super(...params);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NotFoundError);
    }

    this.name = "NotFoundError";
    this.message = message;
  }
}

export const addUrlParam = (urlStr, key, value) => {
  const url = new URL(urlStr);
  const params = url.searchParams;
  params.append(key, value);
  url.search = params.toString();
  return url.toString();
};

export const fetchWithAuth = auth => (url, props = {}) => {
  const scopedUrl = auth.currentOrganization
    ? addUrlParam(url, "org-id", auth.currentOrganization.id)
    : url;

  // axios will remove Content-Type header if request body is undefined
  // some auth backend services rely on Content-Type header,
  // so pass empty object to ensure Content-Type header is send
  const data =
    includes(props.method, ["GET", "DELETE", "POST", "PUT"]) && !props?.data
      ? {}
      : props.data;

  props.data = data;

  return axios(
    scopedUrl,
    mergeDeepRight(
      {
        crossDomain: true,
        headers: {
          Authorization: `Bearer ${auth.user.token}`,
        },
      },
      props,
    ),
  );
};

const getSecurityUserUrl = userId => `${AUTH_BASE_URL}/users/${userId}`;
const getOrganizationUrl = userId => `${AUTH_BASE_URL}/organizations/${userId}`;

export const fetchSecurityUser = auth => userId => {
  const url = getSecurityUserUrl(userId);
  return doGet(auth)(url);
};

export const fetchOrganization = auth => orgId => {
  const url = getOrganizationUrl(orgId);
  return doGet(auth)(url);
};

export const useWithAuth = wrapped => {
  const auth = useSelector(state => state.auth);
  return wrapped(auth);
};

export const checkForError = response => {
  throw new FetchError(response.status, response.statusText);
};

export const getQueryParams = pipe(pickBy(notBlank), querystring.encode);
export const addQueryParams = (url, params) => {
  const queryParams = getQueryParams(params);
  if (isBlank(queryParams)) {
    return url;
  }
  return `${url}?${queryParams}`;
};

export const formatQuery = cond =>
  ifElse(allPass([notBlank, cond]), identity, always(""));

export const parseTitleQuery = formatQuery(notUUID);
export const parseIdQuery = formatQuery(isUUID);
